import { useEffect, useLayoutEffect, useRef } from 'preact/hooks';
import { CAT_IDS } from '../race/types';
import { Badges } from './Badges';
import { BetsPanel } from './BetsPanel';
import { BetTicker } from './BetTicker';
import { Captions } from './Captions';
import { CatCard } from './CatCard';
import { Controls } from './Controls';
import { HistoryPanel } from './HistoryPanel';
import { NameTags, type TagPosition } from './NameTags';
import { PhotoFinish } from './PhotoFinishPanel';
import { RaceMap } from './RaceMap';
import { Results } from './Results';
import { Standings } from './Standings';
import { TitleScreen } from './TitleScreen';
import type { HudStore } from './store';

export function Hud({ store, onStart, tags }: { store: HudStore; onStart: () => void; tags: { value: TagPosition[] } }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!store.started.value || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Escape' && store.betsOpen.value) { store.betsOpen.value = false; return; }
      if (e.key === 'Escape' && store.historyOpen.value) { store.historyOpen.value = false; return; }
      if (e.key === 'Escape' && store.cardOpen.value) { store.cardOpen.value = null; return; }
      const n = Number(e.key);
      if (n >= 1 && n <= 6 && !(e.target instanceof HTMLInputElement) && !store.betsOpen.value) store.follow(CAT_IDS[n - 1]);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store]);
  // How far the top status and the bottom bar reach into the screen, as --top-h and --bottom-h.
  // The phone layouts in hud.css place the standings, captions and results between them.
  const hudRef = useRef<HTMLDivElement>(null);
  const started = store.started.value;
  useLayoutEffect(() => {
    const hud = hudRef.current;
    if (!hud) return;
    const top = hud.querySelector<HTMLElement>(':scope > .top')!;
    const bottom = hud.querySelector<HTMLElement>(':scope > .bottom')!;
    const measure = () => {
      const h = hud.getBoundingClientRect();
      hud.style.setProperty('--top-h', `${Math.round(top.getBoundingClientRect().bottom - h.top)}px`);
      hud.style.setProperty('--bottom-h', `${Math.round(h.bottom - bottom.getBoundingClientRect().top)}px`);
    };
    measure();
    const ro = new ResizeObserver(measure);
    for (const el of [hud, top, bottom]) ro.observe(el);
    return () => ro.disconnect();
  }, [started]);
  if (!store.started.value) return <TitleScreen store={store} onStart={onStart} />;
  const lost = store.snapshot.value?.signal === 'lost';
  const results = store.snapshot.value?.phase === 'finished' && !!store.snapshot.value.results?.length;
  return (
    <div ref={hudRef} class={`hud${lost ? ' dimmed' : ''}${store.restrained.value ? ' restrained' : ''}${results ? ' showing-results' : ''}`}>
      <NameTags store={store} positions={tags.value} />
      <div class="top"><Badges store={store} /><BetTicker store={store} /></div>
      <Standings store={store} />
      <Captions store={store} />
      <div class="results-side"><PhotoFinish store={store} /><Results store={store} /></div>
      <CatCard store={store} />
      <HistoryPanel store={store} />
      <BetsPanel store={store} />
      <div class="bottom"><RaceMap store={store} /><Controls store={store} /></div>
    </div>
  );
}
